/**
 * Handles the "ask the customer to rate the businesses in their most
 * recently completed order" flow.
 *
 * Trigger points:
 *   - App boot (see App.js), so it also catches orders that completed
 *     while the app was closed.
 *   - The order tracking page, right when the order the customer is
 *     looking at flips to "completed" (see TrackOrder.js).
 *
 * The server (ReviewController::pendingBusinessRatings) only ever reports
 * the single most recently completed order's still-unrated items - if
 * that order has already been fully rated, it deliberately does not fall
 * back to an older order. Locally we just throttle how often we re-show
 * the modal for the *same* order: once shown, we wait
 * REMIND_AFTER_HOURS hours before showing it again for that order. A
 * newly (or differently) completed order always shows immediately.
 */
import { app, payload } from "modular-openscriptjs";
import { api } from "./declarations";
import { routes } from "./backendRoutes";
import { store, getStore, sendEvent, consoleErrorOnLocal } from "./helpers";

const STORAGE_KEY = "business_rating_prompt_state";
const REMIND_AFTER_HOURS = 3;

const defaultState = () => ({
    orderId: null,
    lastShownAt: null,
});

async function getState() {
    const state = await getStore(STORAGE_KEY);
    return { ...defaultState(), ...(state || {}) };
}

async function saveState(state) {
    await store(STORAGE_KEY, state);
}

/**
 * Fetches the current customer's pending business ratings and, if it's
 * time, broadcasts the event that opens the rating modal.
 */
export async function checkPendingBusinessRatings() {
    let res;

    try {
        res = await api.get(routes.pendingBusinessRatings, {}, { silent: true });
    } catch (e) {
        consoleErrorOnLocal("checkPendingBusinessRatings failed", e);
        return;
    }

    if (!res || !res.data) return;

    const { order, items } = res.data;

    if (!order || !Array.isArray(items) || items.length === 0) return;

    const state = await getState();
    const now = Date.now();

    const isNewOrder = state.orderId !== order.id;
    const dueForReminder =
        !isNewOrder &&
        state.lastShownAt &&
        now - state.lastShownAt >= REMIND_AFTER_HOURS * 60 * 60 * 1000;

    if (!isNewOrder && !dueForReminder) return;

    await saveState({ orderId: order.id, lastShownAt: now });

    sendEvent(app("$e").businessRating.needs.prompt, payload({ order, items }));
}
